import {
  ImagePortrait, PageHeader, TableBodyContainer, TableBodyLine,
  TableCellWithAdditionalCount, TableContainer, TablePeriodFilter, Text1
} from 'components/shared'
import React, { useEffect } from 'react'
import styles from './PersonsPage.module.scss'
import cl from 'classnames'
import { inject } from 'mobx-react'
import { PersonsTableSortProps, PERSONS_TABLE_STORE, PERSON_STORE, ROUTING_STORE } from 'store'
import { observer } from 'mobx-react-lite'
import { PersonsPageProps, personsTableWidths } from './PersonsPage.types'
import { getMainImageSrcForObj, noImageAvatar } from 'utilities'
import moment from 'moment'
import { TableHeader } from 'components/shared/TableHeader'
import { TableTagsCell } from 'components/shared/TableTagsCell'
import { PersonRoles } from 'models'
import { IconPeople } from 'assets'
import { WarningMessage } from 'components'
import tableBodyContainerStyle from '../../../components/shared/TableBodyContainer/TableBodyContainer.module.scss'


const PersonsPageComponent = (props: PersonsPageProps) => {
  const {
    [PERSON_STORE]: {
      getAll
    },
    [PERSONS_TABLE_STORE]: {
      searchResults,
      searchProps,
      onSearchPropsChange,
      getSortDirection,
      handleSortDirectionChange,
      isColumnMarked
    },
    [ROUTING_STORE]: {
      push
    }
  } = props

  useEffect(() => getAll(false, true), [getAll])

  const scrollbarSpacing = 4.8

  return (
    <div className={styles.container}>
      <PageHeader
        navSegments={[{
          label: 'People',
          icon: IconPeople
        }]}
        buttons={[{
          preset: 'primary',
          children: 'Add a new person',
          onClick: () => push('/persons/add')
        }]}
      />
      <TableContainer style={ { overflowX: 'auto' } } className={styles.thinScroll} >
        <TableHeader
          tableColumns={[
            {
              label: 'name',
              // widthPercentage: personsTableWidths.name,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.name,
                onChange: name => onSearchPropsChange({ name })
              },
              sortDirection: getSortDirection(PersonsTableSortProps.NAME),
              setSortDirection: handleSortDirectionChange(PersonsTableSortProps.NAME),
              isColumnMarked: isColumnMarked(PersonsTableSortProps.NAME),
              widthPx: personsTableWidths.name
            },
            {
              label: 'roles',
              // widthPercentage: personsTableWidths.roles,
              selectProps: {
                options: Object.values(PersonRoles)
                  .map(role => ({ value: role, label: role })),
                noneLabel: 'All',
                onChange: role => onSearchPropsChange({ role }),
                value: searchProps.role
              },
              sortDirection: getSortDirection(PersonsTableSortProps.ROLES),
              setSortDirection: handleSortDirectionChange(PersonsTableSortProps.ROLES),
              isColumnMarked: isColumnMarked(PersonsTableSortProps.ROLES),
              widthPx: personsTableWidths.roles
            },
            {
              label: 'projects',
              // widthPercentage: personsTableWidths.projects,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.projectName,
                onChange: projectName => onSearchPropsChange({ projectName })
              },
              sortDirection: getSortDirection(PersonsTableSortProps.PROJECTS),
              setSortDirection: handleSortDirectionChange(PersonsTableSortProps.PROJECTS),
              isColumnMarked: isColumnMarked(PersonsTableSortProps.PROJECTS),
              widthPx: personsTableWidths.projects
            },
            {
              label: 'added at',
              // widthPercentage: personsTableWidths.addedAt,
              filterComponent: (
                <TablePeriodFilter
                  period={searchProps.minAddedAt
                    ? {
                      start: searchProps.minAddedAt,
                      end: searchProps.maxAddedAt
                    }
                    : null}
                  setPeriod={period => {
                    const { start, end } = period || {}
                    onSearchPropsChange({
                      minAddedAt: start || null,
                      maxAddedAt: end || null
                    })
                  }}
                />
              ),
              sortDirection: getSortDirection(PersonsTableSortProps.ADDED_AT),
              setSortDirection: handleSortDirectionChange(PersonsTableSortProps.ADDED_AT),
              isColumnMarked: isColumnMarked(PersonsTableSortProps.ADDED_AT),
              widthPx: personsTableWidths.addedAt
            },
            {
              label: 'tags',
              // widthPercentage: personsTableWidths.tags,
              inputProps: {
                placeholder: 'Search...',
                value: searchProps.tags,
                onChange: tags => onSearchPropsChange({ tags })
              },
              widthPx: personsTableWidths.tags + scrollbarSpacing
            }
          ]}
        />
        <TableBodyContainer className={tableBodyContainerStyle.tableBodyContainer}>
          {searchResults.map(person => {
            return (
              <TableBodyLine
                key={person.id}
                cells={[
                  {
                    // widthPercentage: personsTableWidths.name,
                    className: cl(styles.nameCell, styles.clickable),
                    onClick: () => window.location.href = (`/persons/${person.id}`),
                    children: (
                      <>
                        <ImagePortrait
                          alt={person.name}
                          src={getMainImageSrcForObj(person) || noImageAvatar}
                        />
                        <span>{person.name}</span>
                        <WarningMessage
                          object={person}
                          objectType='person'
                        />
                      </>
                    ),
                    isColumnMarked: isColumnMarked(PersonsTableSortProps.NAME),
                    widthPx: personsTableWidths.name
                  },
                  {
                    // widthPercentage: personsTableWidths.roles,
                    children: <Text1 overflowEllipsis>{person.rolesAsString}</Text1>,
                    isColumnMarked: isColumnMarked(PersonsTableSortProps.ROLES),
                    widthPx: personsTableWidths.roles
                  },
                  {
                    // widthPercentage: personsTableWidths.projects,
                    children: (
                      <TableCellWithAdditionalCount
                        text={person.projectsInfoCol.projectName}
                        additionalCount={person.projectsInfoCol.additionalCount}
                      />
                    ),
                    isColumnMarked: isColumnMarked(PersonsTableSortProps.PROJECTS),
                    widthPx: personsTableWidths.projects
                  },
                  {
                    // widthPercentage: personsTableWidths.addedAt,
                    children: moment(person.createdDate).format('HH:mm, DD.MM.YYYY'),
                    isColumnMarked: isColumnMarked(PersonsTableSortProps.ADDED_AT),
                    widthPx: personsTableWidths.addedAt
                  },
                  {
                    // widthPercentage: personsTableWidths.tags,
                    children: <TableTagsCell tags={person.tags} />,
                    widthPx: personsTableWidths.tags
                  }
                ]}
              />
            )
          })}
        </TableBodyContainer>
      </TableContainer>
    </div>
  )
}

const PersonsPage = inject(
  PERSON_STORE,
  PERSONS_TABLE_STORE,
  ROUTING_STORE
)(observer(PersonsPageComponent))
export { PersonsPage }
